import { Suspense, lazy, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import type { SaleEngine } from '../state/useSaleEngine'
import { useAuth } from '../state/auth'
import { fill, phrases } from '../state/phrases'
import { createStickyScroll, type StickyScroll } from '../state/stickyScroll'
/**
 * The three forms a sale reaches LATER, each fetched at the step that needs it.
 *
 * Together they are the largest part of the bundle - the confirm card alone is bigger than the whole
 * conversation - and none of them can be reached until the customer has chosen a device and answered
 * several questions. Loading them during startup delays the screen they have not asked for yet.
 */
const ConfirmSale = lazy(() => import('./ConfirmSale'))
const SignInFlow = lazy(() => import('./SignInFlow'))
const SpecPicker = lazy(() => import('./SpecPicker'))

/**
 * The conversation: the welcome, the messages, the choices for the current question, and whichever form
 * the step calls for.
 *
 * Every message is rendered as TEXT. Nothing the server or the assistant returns is ever inserted as
 * markup, so a reply cannot introduce a link, a script or a form of its own into a page where the next
 * thing a customer does is type their mobile number.
 */
export default function Conversation({ engine }: { engine: SaleEngine }) {
  const { sale, question, pending, thinking, busy } = engine
  const { customer } = useAuth()
  const endOfMessages = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)

  /** Follows the newest content until the customer scrolls up. See state/stickyScroll.ts for the rules. */
  const sticky = useRef<StickyScroll | null>(null)

  /**
   * Follow the conversation as it GROWS, not merely when a message is added.
   *
   * Scrolling on the message count alone was not enough, and "Confirm your sale request" was the proof:
   * the step changes, the effect runs, and only THEN does ConfirmSale fetch the customer's addresses and
   * render them - so the form grows after the scroll has already happened and its heading sits below the
   * fold. The same is true of the specification step, which loads the platform's 165 processors before it
   * can draw itself. A ResizeObserver waits for the content to settle instead of guessing when it has.
   */
  useEffect(() => {
    const inner = content.current
    const scroller = inner?.closest('.chat') as HTMLElement | null
    if (!inner || !scroller) return

    const follower = createStickyScroll(scroller)
    sticky.current = follower

    const onScroll = () => follower.onScroll()
    scroller.addEventListener('scroll', onScroll, { passive: true })

    const observer = new ResizeObserver(() => follower.follow())
    observer.observe(inner)

    return () => {
      scroller.removeEventListener('scroll', onScroll)
      observer.disconnect()
      sticky.current = null
    }
  }, [])

  // A new message, a new step or the typing indicator means the customer is being ADDRESSED, not merely
  // shown more, so the view re-pins itself even if they had scrolled up to check something.
  useEffect(() => {
    sticky.current?.pin()
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    endOfMessages.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' })
  }, [sale.messages.length, thinking, sale.step])

  const showWelcome = sale.messages.length === 0

  return (
    <div className="chat-inner" id="saleView" ref={content}>
      {showWelcome && (
        <div className="welcome">
          <span className="spark" aria-hidden="true">
            ✦
          </span>
          <h2>What would you like to sell?</h2>
          <p>Tell me what you have. I'll find it in Cashit's catalogue and get you a real price.</p>
        </div>
      )}

      <div className="messages">
        {sale.messages.map((message) => (
          <div key={message.id} className={`message${message.role === 'user' ? ' mine' : ''}`}>
            {message.role === 'assistant' && (
              <span className="assistant-icon" aria-hidden="true">
                ↗
              </span>
            )}
            <div className="bubble">{message.text}</div>
          </div>
        ))}

        {thinking && (
          <div className="message thinking">
            <span className="assistant-icon" aria-hidden="true">
              ↗
            </span>
            <span className="dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span className="sr-only">SellGPT is typing</span>
          </div>
        )}
      </div>

      {/* The assistant's latest reply, announced once. The message list itself is not a live region:
          re-announcing the whole conversation on every turn is unusable with a screen reader. */}
      <p className="sr-only" aria-live="polite">
        {sale.messages.at(-1)?.role === 'assistant' ? sale.messages.at(-1)?.text : ''}
      </p>

      {question && question.choices.length > 0 && (
        <>
          <div className="choices" aria-label={question.prompt}>
            {question.choices.map((choice) => {
              const id = Number(choice.key.split(':')[1])
              const selected = question.multi ? pending.includes(id) : choice.selected
              return (
                <button
                  key={choice.key}
                  type="button"
                  className={`choice${selected ? ' selected' : ''}`}
                  aria-pressed={question.multi ? selected : undefined}
                  disabled={busy}
                  onClick={() => engine.choose(choice.key)}
                >
                  {choice.label}
                  {choice.hint && <small>{choice.hint}</small>}
                </button>
              )
            })}
          </div>

          {question.multi && (
            <div className="choices-confirm">
              {/* The label follows what is TICKED RIGHT NOW, not what has been stored. Reading the
                  stored answers made this button say "I have none of these" while three accessories
                  were visibly selected. */}
              <button type="button" className="primary" disabled={busy} onClick={engine.confirmMulti}>
                {(pending.length > 0 ? question.confirmLabel : question.confirmEmptyLabel) ?? 'Continue'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Signing in on its own, asked for from the sidebar rather than reached through a sale. Shown at
          the top of the conversation because that is where the customer is looking after tapping it, and
          suppressed once a sale reaches its own identify step so the same form is never on screen twice. */}
      {engine.signInRequested && !customer && sale.step !== 'identify' && (
        <div className="signin-standalone">
          {/* Its own boundary: this form is lazy like the others, and it renders outside #special. */}
          <Suspense fallback={<div className="lead" aria-busy="true" />}>
            <SignInFlow engine={engine} purpose="account" onSignedIn={engine.dismissSignIn} />
          </Suspense>
          <button type="button" className="linkish" onClick={engine.dismissSignIn}>
            Not now
          </button>
        </div>
      )}

      <Suspense fallback={<div className="lead" aria-busy="true" />}>
      <div id="special">
        {/* The specification: what Cashit has for this model, with each line changeable. A form rather
            than chips - the full platform list runs to 165 processors. */}
        {sale.step === 'components' && <SpecPicker engine={engine} />}

        {/* Identify: a guest verifies a mobile number here; a customer who is already signed in skips
            straight to confirming, because there is nothing left to ask them. */}
        {sale.step === 'identify' &&
          (customer ? <ConfirmSale engine={engine} /> : <SignInFlow engine={engine} />)}

        {sale.step === 'confirm' && <ConfirmSale engine={engine} />}

        {sale.step === 'done' && sale.leadNumber && (
          <>
            {/* The lead number and the source stay exactly as they are in every language: one is the
                customer's own reference for this sale, the other is a hostname. */}
            <div className="lead-result" role="status">
              {fill(phrases(engine.language).doneDetail, {
                leadNumber: sale.leadNumber,
                source: engine.config?.leadSource ?? 'sellgpt.in',
              })}
            </div>
            {/* The sale does not end at "created" - this is where the customer follows it to completion,
                and accepts or declines a revised amount if the inspection finds something. */}
            <div className="choices">
              <Link className="choice" to={`/sale/${encodeURIComponent(sale.leadNumber)}`}>
                {phrases(engine.language).trackSale}
                <small>{phrases(engine.language).trackSaleHint}</small>
              </Link>
            </div>
          </>
        )}
      </div>
      </Suspense>

      <div ref={endOfMessages} />
    </div>
  )
}
